import area from '@turf/area';
import { polygon } from '@turf/helpers';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import type { Project } from '../../../projects';
import type { NetworkStats } from '../../../spatial/types';

interface HeadlineMetric {
  value: string;
  title: string;
  subtext?: string;
}

interface ProjectAreaResult {
  areaKm2: number | null;
  source: 'customBounds' | 'bounds' | null;
}

interface UrbanNetworkHeadlineMetrics {
  totalLength: HeadlineMetric;
  segments: HeadlineMetric;
  intersections: HeadlineMetric;
  roadDensity: HeadlineMetric;
}

const INTEGER_FORMATTER = new Intl.NumberFormat('es-AR');
const LENGTH_FORMATTER = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const DENSITY_FORMATTER = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function getUrbanNetworkHeadlineMetrics(
  networkStats: NetworkStats | null,
  project: Project | null
): UrbanNetworkHeadlineMetrics {
  const totalLengthKm = Number(networkStats?.totalLengthKm) || 0;
  const segmentsCount = networkStats?.edgeCount ?? 0;
  const intersectionsCount = networkStats?.intersectionsCount ?? 0;
  const projectArea = getProjectAreaKm2(project);
  const roadDensity = getRoadDensity(totalLengthKm, projectArea.areaKm2);

  return {
    totalLength: {
      value: `${LENGTH_FORMATTER.format(totalLengthKm)} km`,
      title: 'Longitud acumulada de segmentos activos de la red urbana.',
      subtext: totalLengthKm > 0 ? 'Longitud total activa' : undefined,
    },
    segments: {
      value: INTEGER_FORMATTER.format(segmentsCount),
      title: 'Cantidad total de segmentos activos de la red urbana.',
    },
    intersections: {
      value: INTEGER_FORMATTER.format(intersectionsCount),
      title: 'Nodos topológicos con grado mayor o igual a 3.',
    },
    roadDensity: roadDensity == null
      ? {
          value: '—',
          title: 'Densidad no disponible: el área del proyecto no es válida o no está definida.',
          subtext: undefined,
        }
      : {
          value: `${DENSITY_FORMATTER.format(roadDensity)} km/km²`,
          title:
            projectArea.source === 'customBounds'
              ? 'Densidad vial calculada con el área exacta del proyecto (customBounds).'
              : 'Densidad vial calculada con fallback al bounding box del proyecto.',
          subtext:
            projectArea.source === 'customBounds'
              ? 'Área proyecto'
              : undefined,
        },
  };
}

function getRoadDensity(totalLengthKm: number, areaKm2: number | null): number | null {
  if (!Number.isFinite(totalLengthKm) || !Number.isFinite(areaKm2) || !areaKm2 || areaKm2 <= 0) {
    return null;
  }

  return totalLengthKm / areaKm2;
}

function getProjectAreaKm2(project: Project | null): ProjectAreaResult {
  if (!project) {
    return { areaKm2: null, source: null };
  }

  const customBoundsArea = getGeoJsonAreaKm2(project.customBounds);
  if (customBoundsArea && customBoundsArea > 0) {
    return { areaKm2: customBoundsArea, source: 'customBounds' };
  }

  const bounds = project.bounds;
  if (
    !bounds ||
    !Number.isFinite(bounds.minLng) ||
    !Number.isFinite(bounds.minLat) ||
    !Number.isFinite(bounds.maxLng) ||
    !Number.isFinite(bounds.maxLat) ||
    bounds.minLng >= bounds.maxLng ||
    bounds.minLat >= bounds.maxLat
  ) {
    return { areaKm2: null, source: null };
  }

  // Fallback documentado: si customBounds no existe o no es confiable, usamos
  // el bounding box rectangular del proyecto para evitar fabricar un valor falso.
  const bboxPolygon = polygon([[
    [bounds.minLng, bounds.minLat],
    [bounds.maxLng, bounds.minLat],
    [bounds.maxLng, bounds.maxLat],
    [bounds.minLng, bounds.maxLat],
    [bounds.minLng, bounds.minLat],
  ]]);

  const bboxAreaKm2 = area(bboxPolygon) / 1_000_000;
  return Number.isFinite(bboxAreaKm2) && bboxAreaKm2 > 0
    ? { areaKm2: bboxAreaKm2, source: 'bounds' }
    : { areaKm2: null, source: null };
}

function getGeoJsonAreaKm2(
  geometry: Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon | null | undefined
): number | null {
  if (!geometry) {
    return null;
  }

  const geometryType = 'type' in geometry ? geometry.type : null;
  if (
    geometryType !== 'Feature' &&
    geometryType !== 'Polygon' &&
    geometryType !== 'MultiPolygon'
  ) {
    return null;
  }

  try {
    const areaKm2 = area(geometry as Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon) / 1_000_000;
    return Number.isFinite(areaKm2) && areaKm2 > 0 ? areaKm2 : null;
  } catch {
    return null;
  }
}
